import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppErrorType } from '@typesApp/AppError'
import { InsuranceStatusErrorCode, InsuranceUsage } from '@typesApp/ClaimData'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { APPLY_INSURANCE } from '@constants/index'
import { config } from '@config/config'
import { useInteractionContext } from '@context/interaction-context/useInteractionContext'
import { usePollClaimRecord } from '@hooks/poll-claimrecord/usePollClaimRecord'
import { useActiveData } from '@hooks/useActiveData'
import { useCart } from '@hooks/useCart'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { useInvokeInsuranceActionListeners } from '@hooks/useInvokeInsuranceActionListener'
import { useChangeInsuranceStatusMutation } from '@redux/queries/claimRecords'
import { setAppError } from '@redux/slices/workflowSlice'
import {
  isConflictError,
  isCustomerChangedError,
  isErrorWithClaimRecord,
  isGatewayTimeout,
  isResourceNotFoundError,
} from '@utils/errorUtil'
import { resetClaimRecordState } from '@utils/resetClaimRecordState'

const STOP_ERROR_CODES: InsuranceStatusErrorCode[] = [
  InsuranceStatusErrorCode.CART_QUOTE_IN_PROGRESS,
  InsuranceStatusErrorCode.CLAIM_NULLIFY_IN_PROGRESS,
  InsuranceStatusErrorCode.CLAIM_SUBMIT_IN_PROGRESS,
  InsuranceStatusErrorCode.CART_QUOTE_FAILED,
]

type UseInsuranceUsageReturn = {
  isLoading: boolean
  enableInsuranceUsage: () => Promise<void>
  disableInsuranceUsage: () => Promise<void>
}

export const useInsuranceUsage = (): UseInsuranceUsageReturn => {
  const dispatch = useDispatch()
  const { isCart, refreshCart } = useCart()
  const [isLoading, setLoading] = useState(false)
  const { fetchActiveData } = useActiveData()
  const { claimRecord: cachedClaimRecord } = useClaimRecord()
  const { pushFallbackInteraction } = useInteractionContext()
  const { pollClaimRecord } = usePollClaimRecord(APPLY_INSURANCE.MAX_WAIT_TIME_TOGGLE_MS)
  const invokeListeners = useInvokeInsuranceActionListeners()
  const [triggerChangeStatus] = useChangeInsuranceStatusMutation()

  const enableInsuranceUsage = (): Promise<void> => changeInsuranceUsage(InsuranceUsage.INSURANCE_ENABLED)

  const disableInsuranceUsage = (): Promise<void> => changeInsuranceUsage(InsuranceUsage.INSURANCE_DISABLED)

  const changeInsuranceUsage = async (insuranceUsage: InsuranceUsage): Promise<void> => {
    if (!cachedClaimRecord || isLoading) return Promise.resolve()

    const isResultReady = (claimRecord: ClaimRecord): boolean => {
      return (
        claimRecord?.claimData?.useInsuranceStatus === insuranceUsage ||
        STOP_ERROR_CODES.includes(claimRecord?.claimData?.insuranceStatusErrorCode ?? InsuranceStatusErrorCode.NONE)
      )
    }

    const handleResult = (claimRecord: ClaimRecord, isTimeExceeded?: boolean) => {
      if (isTimeExceeded) {
        handleError()
        return
      }

      setLoading(false)

      // do not show error page if claim application still in progress
      if (
        STOP_ERROR_CODES.includes(claimRecord?.claimData?.insuranceStatusErrorCode ?? InsuranceStatusErrorCode.NONE)
      ) {
        return
      }

      const isSuccess = claimRecord?.claimData?.useInsuranceStatus === insuranceUsage
      if (isSuccess) {
        triggerCommerceAction()
      }
    }

    const handleError = async (error?: unknown) => {
      setLoading(false)

      if (isCustomerChangedError(error) || isResourceNotFoundError(error)) {
        resetClaimRecordState(dispatch)
        triggerCommerceAction()
      } else {
        const isEnable = insuranceUsage === InsuranceUsage.INSURANCE_ENABLED
        pushFallbackInteraction('fallback', {
          reason: isEnable ? 'enable-insurance-error' : 'disable-insurance-error',
          error,
        })
        dispatch(setAppError(AppErrorType.GENERIC_ERROR))
      }
    }

    const triggerCommerceAction = async () => {
      if (insuranceUsage === InsuranceUsage.INSURANCE_ENABLED || insuranceUsage === InsuranceUsage.INSURANCE_DISABLED) {
        const action = insuranceUsage === InsuranceUsage.INSURANCE_ENABLED ? 'apply' : 'remove'
        await invokeListeners(action)
      }

      const enableGuard = config.getFeatures()?.enableRefreshCartGuardOnApplyInsurance
      if ((enableGuard && isCart) || !enableGuard) {
        refreshCart()
      }
    }

    const pollClaimRecordForResult = () => {
      pollClaimRecord(isResultReady, handleResult, handleError)
    }

    const doUpdateWithPolling = async () => {
      try {
        // ensure claim record is stll active, customer may have performed actions on another tab
        const activeData = await fetchActiveData()
        if (!activeData?.id) {
          setLoading(false)
          resetClaimRecordState(dispatch)
          triggerCommerceAction()
          return
        }

        const result = await triggerChangeStatus({
          claimRecordId: activeData.id,
          useInsuranceStatus: insuranceUsage,
        }).unwrap()

        if (isResultReady(result)) {
          handleResult(result)
        } else {
          pollClaimRecordForResult()
        }
      } catch (error) {
        handleSubmitError(error)
      }
    }

    const handleSubmitError = (error?: unknown) => {
      // b/e will return 409 if status cannot be changed due
      // to cart quote or claim submission in progress
      if (isGatewayTimeout(error) || isConflictError(error)) {
        pollClaimRecordForResult()
      } else {
        if (isErrorWithClaimRecord(error) && error?.data?.claimRecord) {
          handleResult(error.data.claimRecord)
        } else {
          handleError(error)
        }
      }
    }

    // update insurance usage and poll as required
    setLoading(true)
    return doUpdateWithPolling()
  }

  return {
    isLoading,
    enableInsuranceUsage,
    disableInsuranceUsage,
  }
}
